import { hashTypedData } from 'viem';
import type {
  EIP712TypedData,
  DecodedTransaction,
  DecodedField,
} from '@txviz/core';
import { hexToBytes, sanitizeDisplayString } from '@txviz/core';

/**
 * Compute the EIP-712 hash for typed data.
 *
 * This computes: keccak256("\x19\x01" || domainSeparator || hashStruct(message))
 * which is the exact hash that gets signed.
 */
export function hashEIP712TypedData(typedData: EIP712TypedData): Uint8Array {
  const domain: Record<string, unknown> = {};
  if (typedData.domain.name !== undefined) domain['name'] = typedData.domain.name;
  if (typedData.domain.version !== undefined) domain['version'] = typedData.domain.version;
  if (typedData.domain.chainId !== undefined) domain['chainId'] = typedData.domain.chainId;
  if (typedData.domain.verifyingContract !== undefined)
    domain['verifyingContract'] = typedData.domain.verifyingContract;
  if (typedData.domain.salt !== undefined) domain['salt'] = typedData.domain.salt;

  // viem expects types to include EIP712Domain implicitly
  const types: Record<string, { name: string; type: string }[]> = {};
  for (const [key, value] of Object.entries(typedData.types)) {
    if (key === 'EIP712Domain') continue; // viem handles this internally
    types[key] = value.map((f) => ({ name: f.name, type: f.type }));
  }

  const hashHex = hashTypedData({
    domain,
    types,
    primaryType: typedData.primaryType,
    message: typedData.message as Record<string, unknown>,
  });

  return hexToBytes(hashHex);
}

/**
 * Decode EIP-712 typed data into human-readable form.
 */
export function decodeEIP712TypedData(typedData: EIP712TypedData): DecodedTransaction {
  const fields: DecodedField[] = [];

  // Domain fields
  if (typedData.domain.name !== undefined) {
    fields.push({
      label: 'Domain Name',
      value: sanitizeDisplayString(typedData.domain.name),
      rawValue: typedData.domain.name,
      type: 'text',
    });
  }
  if (typedData.domain.version !== undefined) {
    fields.push({
      label: 'Domain Version',
      value: typedData.domain.version,
      rawValue: typedData.domain.version,
      type: 'text',
    });
  }
  if (typedData.domain.chainId !== undefined) {
    fields.push({
      label: 'Chain ID',
      value: typedData.domain.chainId.toString(),
      rawValue: typedData.domain.chainId.toString(),
      type: 'number',
    });
  }
  if (typedData.domain.verifyingContract !== undefined) {
    fields.push({
      label: 'Verifying Contract',
      value: typedData.domain.verifyingContract,
      rawValue: typedData.domain.verifyingContract,
      type: 'address',
    });
  }

  // Primary type
  fields.push({
    label: 'Primary Type',
    value: sanitizeDisplayString(typedData.primaryType),
    rawValue: typedData.primaryType,
    type: 'text',
  });

  // Message fields — recursively decode the message
  const typeFields = typedData.types[typedData.primaryType];
  if (typeFields) {
    for (const field of typeFields) {
      const value = typedData.message[field.name];
      const displayValue = formatTypedValue(value, field.type);
      fields.push({
        label: sanitizeDisplayString(field.name),
        value: displayValue,
        rawValue: String(value),
        type: inferFieldType(field.type),
      });
    }
  }

  return {
    chain: 'ethereum',
    type: `EIP-712 ${sanitizeDisplayString(typedData.primaryType)}`,
    summary: `Sign typed data: ${sanitizeDisplayString(typedData.primaryType)} (${typedData.domain.name ?? 'unknown domain'})`,
    fields,
    rawHex: '',
  };
}

function formatTypedValue(value: unknown, solidityType: string): string {
  if (value === null || value === undefined) return 'null';

  if (solidityType === 'address') {
    return String(value);
  }
  if (solidityType.startsWith('uint') || solidityType.startsWith('int')) {
    return String(value);
  }
  if (solidityType === 'bool') {
    return String(value);
  }
  if (solidityType === 'string') {
    return sanitizeDisplayString(String(value));
  }
  if (solidityType.startsWith('bytes')) {
    return String(value);
  }

  // For struct/array types, JSON serialize (with BigInt support)
  return sanitizeDisplayString(JSON.stringify(value, (_key, v) =>
    typeof v === 'bigint' ? v.toString() : v as unknown
  ));
}

function inferFieldType(solidityType: string): 'address' | 'value' | 'data' | 'number' | 'text' {
  if (solidityType === 'address') return 'address';
  if (solidityType.startsWith('uint') || solidityType.startsWith('int')) return 'number';
  if (solidityType.startsWith('bytes')) return 'data';
  return 'text';
}
